import React from 'react';
import { Box, Stack, Tab, Tabs, Typography, alpha } from '@mui/material';
import { getBodyColor } from './shared';

const TAB_THEMES = [
  { solid: '#059669', soft: 'rgba(5,150,105,0.1)', glow: 'rgba(5,150,105,0.18)' },
  { solid: '#1F3C88', soft: 'rgba(31,60,136,0.1)', glow: 'rgba(31,60,136,0.18)' },
  { solid: '#C48B32', soft: 'rgba(196,139,50,0.14)', glow: 'rgba(196,139,50,0.18)' },
  { solid: '#2F6B5F', soft: 'rgba(47,107,95,0.12)', glow: 'rgba(47,107,95,0.18)' },
];

const getStageHighlights = (title = '') => {
  const value = String(title).toLowerCase();
  if (value.includes('early') || value.includes('foundational')) {
    return ['Phonics', 'Motor Skills', 'Play-based Learning'];
  }
  if (value.includes('primary')) {
    return ['Reading Fluency', 'Math Confidence', 'Projects'];
  }
  if (value.includes('middle')) {
    return ['Labs', 'Presentation Skills', 'Subject Depth'];
  }
  if (value.includes('senior')) {
    return ['Boards', 'Career Guidance', 'Revision Plans'];
  }
  return ['Learning Goals', 'Classroom Practice', 'Student Growth'];
};

const ProgramTabsSection = ({
  section,
  sectionKey,
  sectionIndex,
  sectionTabs,
  activeTabIndex,
  setActiveTabsBySection,
  bodyContent,
  renderHtmlTextBlock,
  renderPositionableElement,
}) => {
  const bodyColor = getBodyColor(section) || '#64748B';
  const activeTab = sectionTabs[activeTabIndex] || sectionTabs[0];
  const activeTheme = TAB_THEMES[activeTabIndex % TAB_THEMES.length] || TAB_THEMES[0];
  const highlights = getStageHighlights(activeTab?.title);

  return (
    <Box component="section" sx={{ py: { xs: 1, md: 1.6 }, width: '100%' }}>
      <Stack spacing={2.6}>
        {(section.title || bodyContent) && (
          <Box textAlign="center" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {section.title ? (
              <Typography
                sx={{
                  color: '#0F172A',
                  fontWeight: 850,
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  letterSpacing: '-0.03em',
                  mb: 1.2,
                }}
              >
                {section.title}
              </Typography>
            ) : null}
            {bodyContent ? (
              <Box
                sx={{
                  color: bodyColor,
                  fontSize: { xs: '0.98rem', md: '1.08rem' },
                  lineHeight: 1.8,
                  textAlign: 'center',
                  maxWidth: 860,
                  mx: 'auto',
                }}
              >
                {bodyContent}
              </Box>
            ) : null}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={activeTabIndex}
            onChange={(_event, value) =>
              setActiveTabsBySection((prev) => ({ ...prev, [sectionKey]: value }))
            }
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 62,
              p: 0.6,
              borderRadius: '28px',
              background:
                'linear-gradient(135deg, rgba(255,248,241,0.92) 0%, rgba(243,248,255,0.92) 100%)',
              border: `1px solid ${alpha('#0F172A', 0.06)}`,
              boxShadow: '0 14px 30px rgba(15,23,42,0.05)',
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 50,
                borderRadius: '6px',
                px: { xs: 2, md: 2.8 },
                mx: 0.45,
                textTransform: 'none',
                fontWeight: 800,
                color: '#475569',
                fontSize: { xs: '0.92rem', md: '0.98rem' },
                transition: 'all 0.28s ease',
              },
            }}
          >
            {sectionTabs.map((tabItem, tabIndex) => {
              const theme = TAB_THEMES[tabIndex % TAB_THEMES.length] || TAB_THEMES[0];
              const selected = tabIndex === activeTabIndex;

              return (
                <Tab
                  disableRipple
                  key={`${sectionKey}-tab-${tabIndex}`}
                  label={tabItem.title || `Tab ${tabIndex + 1}`}
                  sx={{
                    color: selected ? '#FFFFFF !important' : `${theme.solid} !important`,
                    background: selected
                      ? `linear-gradient(135deg, ${theme.solid} 0%, ${alpha(theme.solid, 0.82)} 100%)`
                      : theme.soft,
                    boxShadow: selected ? `0 14px 28px ${theme.glow}` : 'none',
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0.8,
            flexWrap: 'wrap',
          }}
        >
          {highlights.map((item) => (
            <Box
              key={`${activeTab?.title || 'tab'}-${item}`}
              sx={{
                px: 1.1,
                py: 0.58,
                borderRadius: '6px',
                backgroundColor: activeTheme.soft,
                color: activeTheme.solid,
                fontSize: '0.82rem',
                fontWeight: 800,
                letterSpacing: '0.02em',
              }}
            >
              {item}
            </Box>
          ))}
        </Box>

        {renderPositionableElement({
          section,
          index: sectionIndex,
          elementKey: `${sectionKey}-tab-content`,
          children: (
            <Box
              sx={{
                p: { xs: 2.2, md: 3.2 },
                borderRadius: '34px',
                background: `linear-gradient(135deg, ${alpha(activeTheme.solid, 0.09)} 0%, #FFFFFF 38%, ${alpha(activeTheme.solid, 0.04)} 100%)`,
                border: `1px solid ${alpha(activeTheme.solid, 0.14)}`,
                boxShadow: `0 18px 40px ${alpha(activeTheme.solid, 0.08)}`,
                mt: 0.4,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 1.4, md: 2.2 },
                  gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
                  alignItems: 'start',
                }}
              >
                <Box
                  sx={{
                    borderRadius: '28px',
                    background: `linear-gradient(160deg, ${activeTheme.solid} 0%, ${alpha(activeTheme.solid, 0.86)} 100%)`,
                    color: '#FFFFFF',
                    p: { xs: 1.8, md: 2.2 },
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      opacity: 0.8,
                      mb: 0.8,
                    }}
                  >
                    Academic Stage
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.4rem', md: '1.75rem' },
                      fontWeight: 900,
                      lineHeight: 1.1,
                      mb: 1.2,
                    }}
                  >
                    {activeTab?.title || ''}
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.7, opacity: 0.9 }}>
                    Parents can quickly understand the learning focus, classroom experience, and
                    student support at this stage.
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: '#0F172A',
                      fontWeight: 850,
                      fontSize: { xs: '1.3rem', md: '1.65rem' },
                      mb: 1.2,
                    }}
                  >
                    {activeTab?.title || ''}
                  </Typography>
                  {activeTab?.content ? (
                    renderHtmlTextBlock(activeTab.content, {
                      color: bodyColor,
                      lineHeight: 1.82,
                      fontSize: { xs: '0.98rem', md: '1.04rem' },
                    })
                  ) : (
                    <Typography sx={{ color: bodyColor }}>Add academic content in the editor.</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ),
        })}
      </Stack>
    </Box>
  );
};

export default ProgramTabsSection;
